import { AlertsPortal } from '@/components/ui/alert/AlertsPortal'
import CheckboxInputController from '@/components/ui/inputs/CheckboxInputController'
import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import TextInputController from '@/components/ui/inputs/TextInputController'
import { defaultLpsInfos, INS_OID } from '@/constants/dmp-connect'
import { useLazyCheckInsIdentityQuery } from '@/core/services/dmp-connect-js2'
import { useGetPatientQuery, useUpdatePatientMutation } from '@/core/services/patient'
import { replaceAlerts } from '@/redux/reducers/alerts.reducer'
import { selectDMPConnectTlsiConnector } from '@/redux/reducers/dmp-connect.reducer'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { getGenderOptions, patientGenderToInsSex } from '@/utils/patient'
import { zodResolver } from '@hookform/resolvers/zod'
import { t, Trans } from '@lingui/macro'
import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack } from '@mui/material'
import { skipToken } from '@reduxjs/toolkit/query'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

type CheckINSAndIdentityDialogProps = {
  open: boolean
  onClose: () => void
}

export function CheckInsAndIdentityDialog({ open, onClose }: CheckINSAndIdentityDialogProps) {
  const [checkInsIdentity, { isFetching }] = useLazyCheckInsIdentityQuery()

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <DialogTitle>
        <Trans>Check INS And Identity</Trans>
      </DialogTitle>
      <DialogContent>
        <CheckInsAndIdentityForm checkInsIdentity={checkInsIdentity} formId={FORM_ID} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="neutral">
          <Trans>Cancel</Trans>
        </Button>
        <LoadingButton form={FORM_ID} loading={isFetching} variant="contained" type="submit">
          <Trans>Submit</Trans>
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

const formSchema = z.object({
  insNIR: z.object({
    value: z.string().min(1).length(13),
    key: z.string().length(2).nullable(),
  }),
  name: z.string().min(1),
  surname: z.string().min(1),
  gender: z.enum(['M', 'F', 'I']),
  birthday: z.date().max(new Date()),
  birthPlaceCode: z.string().optional(),
  insNIA: z.boolean().optional(),
})

type CheckINSAndIdentityFormData = z.infer<typeof formSchema>

const FORM_ID = 'CheckINSAndIdentityForm'

const CHECK_INS_AND_IDENTITY_FORM_ALERTS = 'CHECK_INS_AND_IDENTITY_FORM_ALERTS'

type CheckINSAndIdentityFormProps = {
  formId: string
  checkInsIdentity: ReturnType<typeof useLazyCheckInsIdentityQuery>[0]
}

function CheckInsAndIdentityForm({ checkInsIdentity, formId }: CheckINSAndIdentityFormProps) {
  const dispatch = useAppDispatch()
  const { patientId } = useParams<{ patientId: string }>()
  const { enqueueSnackbar } = useSnackbar()

  const { data: patient } = useGetPatientQuery(patientId ?? skipToken)
  const [updatePatient] = useUpdatePatientMutation()

  const { billingNumber, instanceUniqueId: tlsiConnectorId } = useAppSelector(selectDMPConnectTlsiConnector) ?? {}

  const defaultValues: CheckINSAndIdentityFormData = useMemo(
    () => ({
      insNIR: {
        value: patient?.insNIR?.slice(0, 13) ?? '',
        key: patient?.insNIR?.slice(13) ?? '',
      },
      name: patient?.userInfo.name || '',
      surname: patient?.userInfo.surname || '',
      gender: patient?.userInfo.gender || 'I',
      birthday: patient?.userInfo.birthday ? new Date(patient.userInfo.birthday) : new Date(),
      birthPlaceCode: patient?.userInfo.birthPlaceCode || '',
    }),
    [patient]
  )

  const genderOptions = useMemo(getGenderOptions, [])

  const { control, handleSubmit, setValue } = useForm<CheckINSAndIdentityFormData>({ defaultValues, resolver: zodResolver(formSchema) })

  useEffect(() => {
    return () => {
      dispatch(replaceAlerts({ area: CHECK_INS_AND_IDENTITY_FORM_ALERTS, alerts: [] }))
    }
  }, [])
  const onSubmit: SubmitHandler<CheckINSAndIdentityFormData> = async (data) => {
    if (!patient || !tlsiConnectorId) return

    const checkInsIdentityParams = {
      LpsInfos: { ...defaultLpsInfos, s_instance: tlsiConnectorId, s_billingNumber: billingNumber },
      i_sex: patientGenderToInsSex(data.gender),
      s_birthDate: dayjs(data.birthday).format('YYYY-MM-DD'),
      s_birthName: data.surname.trim().toUpperCase(),
      s_birthPlace: data.birthPlaceCode,
      s_given: data.name.trim().toUpperCase(),
      s_ins: data.insNIR.value,
      s_key: data.insNIR.key ?? '',
      s_oid: data.insNIA ? INS_OID['INS-NIA'] : INS_OID['INS-NIR_prod'],
    }

    const { data: result } = await checkInsIdentity(checkInsIdentityParams)
    const isInsIdentityValid = result?.s_status === 'OK'
    if (isInsIdentityValid) {
      dispatch(replaceAlerts({
        area: CHECK_INS_AND_IDENTITY_FORM_ALERTS,
        alerts: [{ message: t({ id: "l'INS est validé", message: 'The INS has been validated' }), type: 'success' }],
      }))
      await updatePatient({
        id: patient._id,
        userInfo: { ...patient.userInfo, gender: data.gender, birthday: data.birthday.toISOString(), birthPlaceCode: data.birthPlaceCode },
        insNIR: data.insNIR.value + data.insNIR.key,
        insOID: INS_OID['INS-NIR_prod'],
      })
      enqueueSnackbar(t`The patient information has been updated successfully.`, { variant: 'success' })
    } else {
      dispatch(replaceAlerts({
        area: CHECK_INS_AND_IDENTITY_FORM_ALERTS,
        alerts: [{ message: t({ id: "L'identité n'a pas pu être validé", message: 'The identity could not be validated' }), type: 'error' }],
      }))
    }
  }

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <AlertsPortal area={CHECK_INS_AND_IDENTITY_FORM_ALERTS} />
        <div>
          <Grid container spacing={3}>
            <Grid item xs={9}>
              <TextInputController control={control} fullWidth label={t`INS registration number`} name="insNIR.value" />
            </Grid>
            <Grid item xs={3}>
              <TextInputController control={control} fullWidth label={t`Key`} name="insNIR.key" />
            </Grid>
            <Grid item xs={12}>
              <CheckboxInputController control={control} label={t`Temporary INS`} name="insNIA" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInputController control={control} fullWidth label={t`Last name`} name="surname" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInputController control={control} fullWidth label={t`First name`} name="name" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                control={control}
                name="birthday"
                render={({ field }) => (
                  <DatePickerInput
                    fullWidth
                    label={t`Birthday`}
                    onChange={(date) => {
                      setValue('birthday', date?.toDate() ?? field.value)
                    }}
                    value={field.value && dayjs(field.value).isValid() ? dayjs(field.value) : null}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectInputController control={control} fullWidth label={t`Gender`} name="gender" options={genderOptions} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInputController control={control} fullWidth label={t`Official geographic code`} name="birthPlaceCode" />
            </Grid>
          </Grid>
        </div>
      </Stack>
    </form>
  )
}
